import { MetricConfigGraphQL, Id } from 'common/types.typescript';
import {
  SolicitationReportingTimestampEnum,
  MetricFilterInput,
  MetricGoalTypeEnum,
} from 'graphql.types';
import type { MetricResultPartial } from './utils/metricInvestigationTable';

export interface GoalData {
  agentsCount: number;
  reachedCount: number;
  goal: number;
  displayValue: string;
  type: MetricGoalTypeEnum;
}

export interface MetricResult {
  value: number;
  title: string;
  displayValue: string;
  totalResults: number;
  higherIsBetter: boolean;
  goalData?: GoalData;
}

export interface MetricFilterParameters {
  filters: MetricFilterInput;
  hasRequiredFilters: boolean;
}

export type ParameterGeneratorFunction = () => MetricFilterParameters;

export const allTeamsOptionValue = 'all_teams';
export const allGroupsOptionValue = 'all_groups';
export const teamOptionValue = 'team';
export const groupOptionValue = 'group';
export const companyOptionValue = 'company';
export const selfOptionValue = 'self';
export const lastYearValue = 'last_year';

export type ComparisonOption = {
  value: string;
  label: string;
  shortLabel: string;
};

export type MetricData = Comparison & MetricResultPartial;

export type Comparison = {
  unit: string;
  value: number | null;
  change: number | null;
  changeText: 'none' | 'decrease' | 'increase';
  comparisonValue: number | null;
  displayValue: string | null;
  displayChangeValue: string | null;
  displayComparisonValue: string | null;
  isNull: boolean;
};

export type QuickActionsProps = {
  metric: MetricConfigGraphQL | null;
  employeeId?: Id | null;
  teamId?: Id | null;
  groupId?: Id | null;
  inGroupId?: Id;
  higherIsBetter: boolean;
  totalResults: number | null;
  title: string;
  comparison: Comparison | null;
  onSelect: () => void;
  displayValue: string | JSX.Element;
  comparisonOption: ComparisonOption;
  setOnOutsideClick?: (canSet: boolean) => void;
  solicitationReportingTimestamp: SolicitationReportingTimestampEnum | null;
};
export type ComparisonByMetric = Record<
  string,
  {
    displayValue: string;
    value: number | null;
    higherIsBetter: boolean;
  }
>;

export interface TableMetricResult {
  value: number;
  title: string;
  displayValue: string;
  totalResults: number;
  higherIsBetter: boolean;
  goalData?: GoalData;
}
export interface TableData {
  [metricConfigId: string]: {
    employees: {
      [employeeId: string]: TableMetricResult;
    };
    teams: {
      [teamId: string]: TableMetricResult;
    };
    groups: {
      [groupId: string]: TableMetricResult;
    };
    previousEmployees: {
      [employeeId: string]: TableMetricResult;
    };
    previousTeams: {
      [teamId: string]: TableMetricResult;
    };
    previousGroups: {
      [groupId: string]: TableMetricResult;
    };
  };
}

export type CellHighlighter = {
  isHighlighted: (
    value: number,
    metricId?: string,
    teamId?: string,
    employeeId?: string
  ) => boolean;
  type?: string;
};

export type HighlightStrategy = (
  data: TableData
) => CellHighlighter & { ignoreThreshhold: boolean };

type Entity = {
  id?: Id;
  name?: string;
  imageSrc?: string;
  teamId?: number;
};

type CompanyEntity = Entity & { id: never };
type TeamEntity = Entity & { owner: { id: Id } };

export type SelectedEntity =
  | { type: 'company'; data: CompanyEntity }
  | { type: 'company' | 'employee'; data: Entity }
  | { type: 'team'; data: TeamEntity }
  | { type: 'group'; data: Entity };

export interface SparkTileProps {
  isActive: boolean;
  metric: MetricConfigGraphQL;
  onClick: (metric: MetricConfigGraphQL) => void;
  comparison?: ComparisonByMetric['metricConfigId'];
}

export type ComparisonType = {
  unit?: string;
  value?: number;
  change?: number;
  changeText: 'none' | 'increase' | 'decrease';
  comparisonValue?: number;
  displayValue?: string;
  displayChangeValue?: string;
  displayComparisonValue?: string;
};

export type MetricDataType = {
  title: string;
  rawValue: number;
  displayValue: string;
  rawDiff: number;
  displayDiff: number | string;
  startDate: string;
  endDate: string;
  employeeIds: (number | string)[];
  timeSeries: any;
  dates: string[];
  higherIsBetter: boolean;
};
